import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, CreditCard, Loader2, LogOut, Zap } from "lucide-react";
import { useAuthContext } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

interface PricingTier {
  id: string;
  tier_name: string;
  description: string | null;
  price_monthly: number | null;
  price_yearly: number | null;
  stripe_price_id_monthly: string | null;
  stripe_price_id_yearly: string | null;
  features: any;
  max_jobs_per_month: number | null;
}

export function SubscriptionGate({ children }: { children: React.ReactNode }) {
  const { user, orgId, subscriptionStatus, isPlatformOwner, loading } = useAuthContext();

  if (loading) return null;
  if (!user) return <>{children}</>;
  if (isPlatformOwner) return <>{children}</>;
  const hasAppAccess = Boolean(orgId) && (subscriptionStatus === "active" || subscriptionStatus === "trial");
  if (hasAppAccess) return <>{children}</>;
  return <PaywallScreen />;
}

function PaywallScreen() {
  const { user, orgId } = useAuthContext();
  const navigate = useNavigate();
  const [tiers, setTiers] = useState<PricingTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [billingPeriod, setBillingPeriod] = useState<"monthly" | "yearly">("monthly");

  useEffect(() => {
    loadTiers();
  }, []);

  const loadTiers = async () => {
    try {
      const { data, error } = await supabase
        .from("pricing_tiers")
        .select("*")
        .order("price_monthly", { ascending: true });
      if (error) throw error;
      setTiers(data || []);
    } catch (err) {
      console.error("Failed to load pricing:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (tierId: string) => {
    if (!orgId) {
      toast.error("Please create an organization first in Settings");
      return;
    }

    try {
      setCheckoutLoading(tierId);
      const tier = tiers.find((t) => t.id === tierId);
      const priceId =
        billingPeriod === "monthly"
          ? tier?.stripe_price_id_monthly
          : tier?.stripe_price_id_yearly;

      if (!priceId) {
        toast.error("Payment not yet configured for this plan. Please contact support.");
        return;
      }

      const { data, error } = await supabase.functions.invoke("create-stripe-checkout", {
        body: { priceId, period: billingPeriod, orgId },
      });

      if (error) throw error;
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err: any) {
      console.error("Checkout error:", err);
      toast.error("Failed to start checkout");
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch (err) {
      console.warn("Sign out error (clearing local session anyway):", err);
    }
    localStorage.removeItem("sb-uqcuajozfzvoiomyxnbg-auth-token");
    toast.success("Signed out");
    navigate("/auth");
  };

  const tierHighlights: Record<string, string[]> = {
    Starter: [
      "Up to 50 jobs/month",
      "AI damage analysis",
      "Proposal generation",
      "Basic CRM",
      "Email support",
    ],
    Pro: [
      "Up to 200 jobs/month",
      "Everything in Starter",
      "AI Sales Co-Pilot",
      "Advanced analytics",
      "Campaign automation",
      "Priority support",
    ],
    Enterprise: [
      "Unlimited jobs",
      "Everything in Pro",
      "Custom integrations",
      "Dedicated account manager",
      "API access",
      "White-label options",
    ],
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/30 flex flex-col">
      {/* Header */}
      <header className="border-b border-border/50 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-gradient-to-br from-primary to-primary-glow">
            <Zap className="w-5 h-5 text-white" />
          </div>
          <span className="font-bold text-lg bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            RoofGenius AI
          </span>
        </div>
        <Button variant="ghost" size="sm" onClick={handleSignOut}>
          <LogOut className="w-4 h-4 mr-2" />
          Sign Out
        </Button>
      </header>

      {/* Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-12">
        <div className="text-center mb-10 max-w-2xl">
          <h1 className="text-3xl md:text-4xl font-bold mb-3">
            Choose Your Plan to Get Started
          </h1>
          <p className="text-muted-foreground text-lg">
            Select a subscription to unlock the full power of RoofGenius AI.
          </p>
        </div>

        {/* Billing Toggle */}
        <div className="flex items-center gap-3 mb-8 p-1 rounded-full bg-muted">
          <button
            onClick={() => setBillingPeriod("monthly")}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              billingPeriod === "monthly"
                ? "bg-primary text-primary-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingPeriod("yearly")}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              billingPeriod === "yearly"
                ? "bg-primary text-primary-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Yearly
            <Badge variant="secondary" className="ml-2 text-xs">
              Save 17%
            </Badge>
          </button>
        </div>

        {loading ? (
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        ) : (
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl w-full">
            {tiers.map((tier, idx) => {
              const isPopular = idx === 1;
              const price =
                billingPeriod === "monthly" ? tier.price_monthly : tier.price_yearly;
              const monthlyEquivalent =
                billingPeriod === "yearly" && tier.price_yearly
                  ? Math.round(tier.price_yearly / 12)
                  : null;
              const features =
                (Array.isArray(tier.features) ? tier.features : null) ||
                tierHighlights[tier.tier_name] ||
                [];

              return (
                <Card
                  key={tier.id}
                  className={`relative flex flex-col ${
                    isPopular
                      ? "border-primary shadow-lg scale-[1.02]"
                      : "border-border"
                  }`}
                >
                  {isPopular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge className="bg-primary text-primary-foreground px-3">
                        Most Popular
                      </Badge>
                    </div>
                  )}
                  <CardHeader className="pb-2">
                    <CardTitle className="text-xl">{tier.tier_name}</CardTitle>
                    <CardDescription>{tier.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="flex-1 flex flex-col">
                    <div className="mb-6">
                      <div className="flex items-baseline gap-1">
                        <span className="text-4xl font-bold">${price || 0}</span>
                        <span className="text-muted-foreground">
                          /{billingPeriod === "monthly" ? "mo" : "yr"}
                        </span>
                      </div>
                      {monthlyEquivalent && (
                        <p className="text-sm text-muted-foreground mt-1">
                          ${monthlyEquivalent}/mo billed annually
                        </p>
                      )}
                    </div>

                    <div className="space-y-3 flex-1 mb-6">
                      {features.map((feature: string, i: number) => (
                        <div key={i} className="flex items-start gap-2">
                          <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                          <span className="text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>

                    <Button
                      className="w-full"
                      variant={isPopular ? "default" : "outline"}
                      onClick={() => handleSubscribe(tier.id)}
                      disabled={checkoutLoading === tier.id}
                    >
                      {checkoutLoading === tier.id ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <CreditCard className="w-4 h-4 mr-2" />
                          Subscribe
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        <p className="text-sm text-muted-foreground mt-8 text-center max-w-md">
          All plans include a 14-day money-back guarantee. Cancel anytime.
          <br />
          Questions?{" "}
          <a href="mailto:sales@roofgeniusai.com" className="text-primary hover:underline">
            Contact sales
          </a>
        </p>
      </div>
    </div>
  );
}
